import { useState, useMemo, useRef, useCallback, useEffect } from 'react'
import * as React from 'react'
import usePrevious from 'hooks/usePrevious'
import SearchMapMarker from './Markers/SearchMapMarker'
import { getMarkersByOffers, decodeOfferIds } from './utils'
import { getPadding, parseBounds, subtractPaddingFromBounds, fitMarkersOnMap, useVisitedMarkersState } from '../../Search/SearchResultsMap/utils'
import debounce from 'lodash.debounce'
import type { GoogleMapProps } from '@react-google-maps/api'
import { GoogleMap } from '@react-google-maps/api'
import TextButton from 'LuxKit/Button/TextButton'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import { EnsureGoogleMapsLoaded, useGoogleMapsContext } from 'contexts/GoogleMapsContext'
import styled from 'styled-components'
import { replaceWithRegion } from 'actions/NavigationActions'
import { connect } from 'react-redux'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import { parseSearchString } from 'lib/url/searchUrlUtils'
import ExperienceSearchMapCarousel from './ExperienceSearchMapCarousel'
import useSelectedItems from '../hooks/useSelectedItems'
import useExperiencesSearchFilters from '../hooks/useExperiencesSearchFilters'
import config from 'constants/config'
import useScreenSize from 'hooks/useScreenSize'
import { unique } from 'lib/array/arrayUtils'
import { mediaQueryDown } from 'components/utils/breakpoint'
import { useIntl } from 'react-intl'

const MapWrapper = styled.div`
  height: 85vh;
  position: relative;
  overflow: hidden;

  ${mediaQueryDown.tablet} {
    height: 100vh;
  }
`

const FloatingBanner = styled.div`
  position: absolute;
  z-index: 1;
  left: 50%;
  transform: translateX(-50%);
  top: 32px;
`

const ColourfulLoadingAnimation = styled(LoadingAnimation)`
  color: ${props => props.theme.palette.brand.primary.normal};
`

const MAP_STYLE = {
  height: '100%',
  width: '100%',
  flexGrow: 1,
}

const MAP_OPTIONS: GoogleMapProps['options'] = {
  gestureHandling: 'greedy',
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  zoomControlOptions: {
    position: 3,
  },
  minZoom: 3,
  maxZoom: 19,
  mapId: config.GOOGLE_MAP_ID,
}

const MARKER_DISPLAY_TIMEOUT = 2000

interface Props {
  windowSearch: string
  filters: App.OfferListFilters
  experiences: Array<App.ExperienceOffer>
  fetching: boolean
  onBoundsChanged?: (newBounds: google.maps.LatLngBounds) => void
  rootRef?: React.RefObject<HTMLDivElement | null>
}

function ExperienceSearchMap(props: Props) {
  const intl = useIntl()
  const { windowSearch, filters, fetching, experiences, onBoundsChanged, rootRef } = props

  const {
    isLoaded: isGoogleMapsLoaded,
  } = useGoogleMapsContext()

  const [map, setMap] = useState<google.maps.Map | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const ignoreNextIdle = useRef<boolean>(true)
  const hasInteracted = useRef<boolean>(false)
  const { setSelectedItems } = useSelectedItems()
  const [visitedMarkerIds, addVisitedMarkerId] = useVisitedMarkersState()
  const [hoveredOffer, setHoveredOffer] = useState<{ id: string, type: string } | undefined>(undefined)
  const [recentSearchResults, setRecentSearchResults] = useState<Array<Array<App.ExperienceOffer>>>([])
  const [markers, setMarkers] = useState<Array<App.SearchExperienceMapMarker>>([])
  const [markerDisplayTimedOut, setMarkerDisplayTimedOut] = useState(false)
  const [waitingForOffersAfterFilterChange, setWaitingForOffersAfterFilterChange] = useState(false)

  const prevFilters = usePrevious(filters)
  const isNewDestination = !!filters.destinationId && filters.destinationId !== prevFilters?.destinationId
  const isNewBounds = !!filters.bounds && filters.bounds !== prevFilters?.bounds

  const lastSearch = useRef({
    destination: false,
    bounds: false,
  })

  if (isNewDestination || isNewBounds) {
    lastSearch.current = {
      destination: isNewDestination,
      bounds: isNewBounds,
    }
  }

  const {
    setBoundsSearchFilter,
  } = useExperiencesSearchFilters()

  const isMobileSize = useScreenSize() === 'mobile'

  const mapOptions = useMemo(() => ({
    ...MAP_OPTIONS,
    zoomControl: !isMobileSize,
  }), [isMobileSize])

  const onInteract = useCallback(() => { hasInteracted.current = true }, [])

  useEffect(() => {
    setMarkerDisplayTimedOut(false)
    const timeoutId = setTimeout(() => {
      setMarkerDisplayTimedOut(true)
    }, MARKER_DISPLAY_TIMEOUT)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [filters.destinationId, filters.bounds])

  useEffect(() => {
    if (!fetching) {
      setRecentSearchResults([experiences])
    }
  }, [fetching, experiences])

  useEffect(() => {
    const filtersChanged = prevFilters?.destinationId !== filters.destinationId ||
      JSON.stringify(prevFilters?.categories) !== JSON.stringify(filters.categories) ||
      prevFilters?.priceLte !== filters.priceLte ||
      prevFilters?.priceGte !== filters.priceGte

    if (filtersChanged) {
      setWaitingForOffersAfterFilterChange(true)
    }
  }, [filters.destinationId, filters.categories, filters.priceLte, filters.priceGte, prevFilters])

  useEffect(() => {
    if (markerDisplayTimedOut) {
      if (waitingForOffersAfterFilterChange && experiences.length > 0) {
        setRecentSearchResults([experiences])
        setWaitingForOffersAfterFilterChange(false)
        return
      }

      // Only combine offers if we're not waiting for offers after a filter change
      if (!waitingForOffersAfterFilterChange) {
        const currentRecentSearchResults =
          recentSearchResults.length > 0 ? recentSearchResults[0] : []
        const nextRecentSearchResults = experiences
        const combinedRecentSearchResults =
          unique([...nextRecentSearchResults, ...currentRecentSearchResults])

        setRecentSearchResults([combinedRecentSearchResults])
      }
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [experiences, markerDisplayTimedOut, waitingForOffersAfterFilterChange])

  const currentOffers = useMemo(() => {
    return recentSearchResults.flat()
  }, [recentSearchResults])

  const currentBounds = useMemo(() => {
    return isGoogleMapsLoaded && filters.bounds !== undefined ? parseBounds(filters.bounds) : undefined
  }, [filters.bounds, isGoogleMapsLoaded])

  const currentMarkers = useMemo(() => {
    const offerMarkers = getMarkersByOffers(currentOffers)
    const markerResult = currentBounds ? offerMarkers.filter((marker) => {
      const markerBounds = new google.maps.LatLngBounds(
        new google.maps.LatLng(marker.latitude, marker.longitude),
        new google.maps.LatLng(marker.latitude, marker.longitude),
      )
      return currentBounds.intersects(markerBounds)
    }) : offerMarkers
    return markerResult
  }, [currentOffers, currentBounds])

  useEffect(() => {
    setMarkers(currentMarkers)
  }, [currentMarkers])

  const params = parseSearchString(windowSearch)

  // Get currently selected offer IDs
  const selectedOfferIds = useMemo(() => (
    decodeOfferIds(params.selectedOfferIds)
  ), [params.selectedOfferIds])
  const onMarkerClick = useCallback((marker: App.SearchExperienceMapMarker) => {
    if (marker.experienceItems) {
      setSelectedItems(marker.experienceItems)
    }
  }, [setSelectedItems])

  const onDeselectOffers = useCallback(() => {
    setSelectedItems([])
  }, [setSelectedItems])

  // Fit the correct bounds on load
  const onLoad = useCallback((map: google.maps.Map) => {
    ignoreNextIdle.current = true
    if (filters.bounds !== undefined && isGoogleMapsLoaded) {
      map.fitBounds(parseBounds(filters.bounds), getPadding(true))
    }
    else {
      if (selectedOfferIds.length > 0 && currentMarkers.length > 0) {
        const selectedMarker = currentMarkers.find((marker) => marker.id.split(',').includes(selectedOfferIds[0].id))
        if (selectedMarker) {
          fitMarkersOnMap(map, currentMarkers, selectedMarker)
        }
      } else {
        fitMarkersOnMap(map, currentMarkers)
      }
    }
    setMap(map)
  }, [filters.bounds, isGoogleMapsLoaded, selectedOfferIds, currentMarkers])

  useEffect(() => {
    if (map && currentMarkers.length > 0 && lastSearch.current.destination && !lastSearch.current.bounds) {
      ignoreNextIdle.current = true
      fitMarkersOnMap(map, currentMarkers)
    }
  }, [map, currentMarkers, lastSearch.current.destination, lastSearch.current.bounds])

  useEffect(() => {
    if (prevFilters && (
      prevFilters.destinationId !== filters.destinationId ||
      JSON.stringify(prevFilters.categories) !== JSON.stringify(filters.categories) ||
      prevFilters.priceLte !== filters.priceLte ||
      prevFilters.priceGte !== filters.priceGte ||
      isNewDestination || isNewBounds
    )) {
      setSelectedItems([])
    }
  }, [filters.destinationId, filters.categories, filters.priceLte, filters.priceGte, prevFilters, setSelectedItems, isNewDestination, isNewBounds])

  const onUnmount = useCallback(() => {
    setMap(null)
  }, [])

  // Maintain mapping of `google.maps.Marker` instances to the source `App.SearchMapMarker` objects
  // so that the clusterer calculator can figure out what offers are in a cluster
  const markerMap = useRef(new Map<google.maps.Marker, App.SearchMapMarker>())
  const onMarkerLoad = useCallback((markerInstance: google.maps.Marker, markerData: App.SearchMapMarker) => {
    markerMap.current.set(markerInstance, markerData)
  }, [])

  const onMarkerUnmount = useCallback((markerInstance: google.maps.Marker) => {
    markerMap.current.delete(markerInstance)
  }, [])

  const handleBoundsChanged = useCallback((newBounds: google.maps.LatLngBounds) => {
    if (!newBounds) { return }
    const bounds = newBounds.toUrlValue()
    setBoundsSearchFilter(bounds)
    if (onBoundsChanged) {
      onBoundsChanged(newBounds)
    }
  }, [setBoundsSearchFilter, onBoundsChanged])

  const onUserMovedMap = useMemo<() => void>(() => debounce(
    () => {
      if (!map || !containerRef.current) return
      const bounds = map.getBounds()
      if (!bounds) return
      const boundsWithoutMargin = subtractPaddingFromBounds(bounds, containerRef.current.getBoundingClientRect())
      handleBoundsChanged(boundsWithoutMargin)
    },
    2000,
    { leading: true, trailing: true },
  ), [map, handleBoundsChanged])

  const onIdle = useCallback(() => {
    if (!ignoreNextIdle.current && hasInteracted.current) {
      onUserMovedMap()
    }
    ignoreNextIdle.current = false
    hasInteracted.current = false
  }, [onUserMovedMap])

  const activeMarkerId = selectedOfferIds.map(selected => selected.id).join(',')
  useEffect(() => {
    if (activeMarkerId) {
      addVisitedMarkerId(activeMarkerId)
    }
  }, [activeMarkerId, addVisitedMarkerId])

  useEffect(() => {
    const root = rootRef?.current
    if (root) {
      const handleOfferHover = (event: CustomEvent) => {
        const id = event.detail.id
        const offerType = event.detail.type
        const persist = event.detail.persist

        if (!id && persist) return
        if (!id && !persist) {
          setHoveredOffer(undefined)
          return
        }
        setHoveredOffer({ id, type: offerType })
      }

      root.addEventListener('offerHover', handleOfferHover as EventListener)
      return () => {
        root.removeEventListener('offerHover', handleOfferHover as EventListener)
      }
    }
  }, [rootRef])

  return (
    <MapWrapper ref={containerRef}>
      <EnsureGoogleMapsLoaded loadingElement={<LoadingIndicator inline />}>
        <GoogleMap
          id="le-map"
          zoom={17}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onClick={onDeselectOffers}
          onDrag={onInteract}
          onZoomChanged={onInteract}
          onHeadingChanged={onInteract}
          onIdle={onIdle}
          mapContainerStyle={MAP_STYLE}
          options={mapOptions}
        >
          {markers.map((marker, index) => {
            const isHovered = hoveredOffer?.id ? marker.id.split(',').includes(hoveredOffer.id) : false
            return (
              <SearchMapMarker
                key={marker.id}
                marker={marker}
                onClick={onMarkerClick}
                onLoad={onMarkerLoad}
                onUnmount={onMarkerUnmount}
                scaleDown={isMobileSize}
                active={selectedOfferIds.some((offerId) => marker.id.split(',').includes(offerId.id))}
                visited={visitedMarkerIds.has(marker.id)}
                isHovered={isHovered}
                index={index}
              />
            )
          })}
        </GoogleMap>
        {fetching && (
          <FloatingBanner>
            <TextButton
              kind="secondary"
              variant="dark"
              nonInteractable={fetching}
              endIcon={<ColourfulLoadingAnimation size={8} />}
            >
              {intl.formatMessage({
                id: 'Imepz7e5m3YifwizOonY',
                defaultMessage: 'Loading experiences',
                description: 'Loading indicator text while experience search results are being fetched on map',
              })}
            </TextButton>
          </FloatingBanner>
        )}
      </EnsureGoogleMapsLoaded>
      {!!experiences.length && <ExperienceSearchMapCarousel />}
    </MapWrapper>
  )
}

function mapStateToProps(state: App.State) {
  const filters = getUrlOfferListFilters(state)

  return {
    windowSearch: state.router.location.search,
    filters,
  }
}

const mapDispatchToProps = {
  replaceWithRegion,
}

export default connect(mapStateToProps, mapDispatchToProps)(ExperienceSearchMap)
